import React, { useMemo, useState } from "react";
import { RotateCcw, ChevronDown } from "lucide-react";
import { PctInput } from "./inputs";

const byName = (key) => (a, b) => (a[key] || "").localeCompare(b[key] || "");

export default function StepReturns({ categories, returnOverrides, setReturnOverrides, investments, setInvestments }) {
    // Collapsible category state — all collapsed by default.
    const [openCats, setOpenCats] = useState({});
    const toggleCat = (id) => setOpenCats((s) => ({ ...s, [id]: !s[id] }));
    const [customOpen, setCustomOpen] = useState(false);

    // Custom sub-categories (added by user in Investments step) that live outside admin config.
    const customRows = useMemo(
        () => (investments || []).filter((r) => r.is_custom).sort(byName("subcategory_name")),
        [investments]
    );

    const currentReturn = (subId, adminDefault) => {
        const ov = returnOverrides?.[subId];
        return ov === undefined || ov === "" ? adminDefault : ov;
    };

    const setOverride = (subId, value) => {
        const next = { ...(returnOverrides || {}) };
        if (value === "" || value === null || value === undefined) delete next[subId];
        else next[subId] = value;
        setReturnOverrides(next);
        // Cascade to any already-added investment row for this sub-category so the Investments/Projection reflect the change.
        if (investments && setInvestments) {
            const numeric = value === "" || value === null || value === undefined ? undefined : Number(value);
            const patched = investments.map((r) =>
                r.subcategory_id === subId
                    ? { ...r, expected_return: numeric === undefined ? r.expected_return : numeric }
                    : r
            );
            setInvestments(patched);
        }
    };

    const setCustomReturn = (subId, value) => {
        if (!investments || !setInvestments) return;
        const numeric = value === "" ? 0 : Number(value);
        setInvestments(investments.map((r) => (r.subcategory_id === subId ? { ...r, expected_return: numeric } : r)));
    };

    const resetOne = (subId) => setOverride(subId, "");

    const resetAll = () => {
        setReturnOverrides({});
        if (investments && setInvestments) {
            // Restore each row's expected_return from admin defaults (leaves custom rows untouched).
            const adminMap = {};
            (categories || []).forEach((cat) => {
                (cat.subcategories || []).forEach((sub) => { adminMap[sub.id] = sub.expected_return; });
            });
            setInvestments(investments.map((r) => (r.is_custom ? r : { ...r, expected_return: adminMap[r.subcategory_id] ?? r.expected_return })));
        }
    };

    const anyOverrides = Object.keys(returnOverrides || {}).length > 0;

    return (
        <div data-testid="step-returns">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
                <div className="max-w-2xl">
                    <div className="text-[11px] uppercase tracking-[0.25em] text-oth-blue font-body mb-2">Step 2 · Assumptions</div>
                    <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">Fine-tune your return assumptions</h2>
                    <p className="text-sm text-oth-ink3 font-body mt-2">
                        These are the annualised return expectations we&apos;ll use to project your wealth. Feel free to override any sub-category to reflect your own view. You can add a header of your investment which might not be listed here in the next step.
                    </p>
                </div>
                {anyOverrides && (
                    <button
                        type="button"
                        onClick={resetAll}
                        className="btn-pill btn-pill-light text-sm"
                        data-testid="reset-all-returns-btn"
                    >
                        <RotateCcw size={14} /> Reset all to defaults
                    </button>
                )}
            </div>

            <div className="space-y-8">
                {(categories || []).map((cat) => {
                    const subs = [...(cat.subcategories || [])].sort(byName("name"));
                    if (subs.length === 0) return null;
                    const isOpen = !!openCats[cat.id];
                    const overriddenCount = subs.reduce((n, s) => n + (returnOverrides?.[s.id] !== undefined && returnOverrides?.[s.id] !== "" ? 1 : 0), 0);
                    return (
                        <section key={cat.id} className="rounded-3xl border border-oth-line/70 bg-white overflow-hidden" data-testid={`returns-category-${cat.id}`}>
                            <button
                                type="button"
                                onClick={() => toggleCat(cat.id)}
                                aria-expanded={isOpen}
                                className={`w-full px-5 md:px-6 py-4 flex items-center justify-between gap-4 bg-[#f7f7f4] hover:bg-[#f0f0ec] transition-colors text-left ${isOpen ? "border-b border-oth-line/60" : ""}`}
                                data-testid={`returns-category-toggle-${cat.id}`}
                            >
                                <div className="min-w-0">
                                    <div className="font-display text-lg font-semibold tracking-tight">{cat.name}</div>
                                    <div className="text-xs text-oth-ink3 font-body mt-1">
                                        {subs.length} sub-{subs.length === 1 ? "category" : "categories"}
                                        {overriddenCount > 0 && <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-oth-blue/10 text-oth-blue text-[10px] font-body uppercase tracking-wider" data-testid={`returns-category-badge-${cat.id}`}>{overriddenCount} customised</span>}
                                    </div>
                                </div>
                                <ChevronDown size={18} className={`shrink-0 text-oth-ink3 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                            </button>
                            {isOpen && (
                                <div className="divide-y divide-oth-line/40">
                                    <div className="hidden md:grid grid-cols-12 px-5 md:px-6 py-2.5 text-[11px] uppercase tracking-[0.18em] text-oth-ink3 font-body bg-[#fafaf7]">
                                        <div className="col-span-6">Sub-category</div>
                                        <div className="col-span-3 text-right">Default</div>
                                        <div className="col-span-3 text-right">Your return %</div>
                                    </div>
                                    {subs.map((sub) => {
                                        const overridden = returnOverrides?.[sub.id] !== undefined && returnOverrides?.[sub.id] !== "";
                                        const current = currentReturn(sub.id, sub.expected_return);
                                        return (
                                            <div key={sub.id} className="grid grid-cols-12 items-center gap-3 px-5 md:px-6 py-3.5" data-testid={`returns-row-${sub.id}`}>
                                                <div className="col-span-12 md:col-span-6 text-sm text-oth-ink2 font-body">
                                                    {sub.name}
                                                    {overridden && (
                                                        <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-oth-blue/10 text-oth-blue text-[10px] font-body uppercase tracking-wider" data-testid={`returns-overridden-${sub.id}`}>Customised</span>
                                                    )}
                                                </div>
                                                <div className="col-span-6 md:col-span-3 text-sm text-oth-ink3 font-body md:text-right tabular-nums" data-testid={`returns-default-${sub.id}`}>
                                                    {Number(sub.expected_return).toFixed(2)}%
                                                </div>
                                                <div className="col-span-6 md:col-span-3 flex items-center justify-end gap-2">
                                                    <div className="w-28">
                                                        <PctInput
                                                            value={current}
                                                            onChange={(v) => setOverride(sub.id, v)}
                                                            placeholder={String(sub.expected_return)}
                                                            testId={`returns-input-${sub.id}`}
                                                        />
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => resetOne(sub.id)}
                                                        disabled={!overridden}
                                                        className="w-8 h-8 rounded-full border border-oth-line/70 flex items-center justify-center text-oth-ink3 hover:text-oth-ink hover:border-oth-blue disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                                        aria-label={`Reset ${sub.name} to default`}
                                                        data-testid={`returns-reset-${sub.id}`}
                                                    >
                                                        <RotateCcw size={13} />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </section>
                    );
                })}

                {customRows.length > 0 && (
                    <section className="rounded-3xl border border-oth-line/70 bg-white overflow-hidden" data-testid="returns-category-custom">
                        <button
                            type="button"
                            onClick={() => setCustomOpen((v) => !v)}
                            aria-expanded={customOpen}
                            className={`w-full px-5 md:px-6 py-4 flex items-center justify-between gap-4 bg-[#f7f7f4] hover:bg-[#f0f0ec] transition-colors text-left ${customOpen ? "border-b border-oth-line/60" : ""}`}
                            data-testid="returns-category-toggle-custom"
                        >
                            <div className="min-w-0">
                                <div className="font-display text-lg font-semibold tracking-tight">Your custom assets</div>
                                <div className="text-xs text-oth-ink3 font-body mt-1">{customRows.length} custom {customRows.length === 1 ? "asset" : "assets"} — set whatever return you expect.</div>
                            </div>
                            <ChevronDown size={18} className={`shrink-0 text-oth-ink3 transition-transform ${customOpen ? "rotate-180" : ""}`} />
                        </button>
                        {customOpen && (
                            <div className="divide-y divide-oth-line/40">
                                {customRows.map((r) => (
                                    <div key={r.subcategory_id} className="grid grid-cols-12 items-center gap-3 px-5 md:px-6 py-3.5" data-testid={`returns-custom-row-${r.subcategory_id}`}>
                                        <div className="col-span-12 md:col-span-6 text-sm text-oth-ink2 font-body">{r.subcategory_name} <span className="text-[11px] text-oth-ink3">· {r.category_name}</span></div>
                                        <div className="col-span-6 md:col-span-3" />
                                        <div className="col-span-6 md:col-span-3 flex items-center justify-end">
                                            <div className="w-28">
                                                <PctInput
                                                    value={r.expected_return}
                                                    onChange={(v) => setCustomReturn(r.subcategory_id, v)}
                                                    placeholder="8"
                                                    testId={`returns-custom-input-${r.subcategory_id}`}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                )}
            </div>

            <div className="mt-8 rounded-2xl border border-oth-line/60 bg-[#f0f0ec] px-5 py-4 text-[12px] leading-relaxed font-body text-oth-ink3">
                <strong className="text-oth-ink2">Tip.</strong> These returns feed the compounding math on the next step. Changes here update any sub-category you&apos;ve already added to your portfolio, and pre-fill the return when you add new ones. You can revisit this step anytime.
            </div>
        </div>
    );
}
